import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Typography, Tabs, Table, Segmented } from 'antd';
import FilterPanel from '../components/FilterPanel';
import api from '../api';

function fmtTime(seconds) {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const METRIC_OPTIONS = ['Raw HT', 'Raw Talk', 'Raw ACW', 'Contacts', 'All'];
const METRIC_KEY     = { 'Raw HT': 'AvgHT', 'Raw Talk': 'AvgTalk', 'Raw ACW': 'AvgACW', 'Contacts': 'Contacts' };
const TAG_VIEWS      = ['Tag Count', 'By Month', 'By Day'];

const DISPO_COLS = [
  { title: 'Campaign Name', dataIndex: 'Campaign',  key: 'Campaign',  ellipsis: true, sorter: (a, b) => a.Campaign.localeCompare(b.Campaign) },
  { title: 'Disp Name',     dataIndex: 'Disp_Name', key: 'Disp_Name', ellipsis: true, sorter: (a, b) => a.Disp_Name.localeCompare(b.Disp_Name) },
  { title: 'Raw HT',        dataIndex: 'AvgHT',     key: 'AvgHT',     width: 90, align: 'right', render: fmtTime, sorter: (a, b) => (a.AvgHT ?? 0) - (b.AvgHT ?? 0) },
  { title: 'Raw Talk',      dataIndex: 'AvgTalk',   key: 'AvgTalk',   width: 90, align: 'right', render: fmtTime, sorter: (a, b) => (a.AvgTalk ?? 0) - (b.AvgTalk ?? 0) },
  { title: 'Raw ACW',       dataIndex: 'AvgACW',    key: 'AvgACW',    width: 90, align: 'right', render: fmtTime, sorter: (a, b) => (a.AvgACW ?? 0) - (b.AvgACW ?? 0) },
  { title: 'Contacts',      dataIndex: 'Contacts',  key: 'Contacts',  width: 90, align: 'right', sorter: (a, b) => (a.Contacts ?? 0) - (b.Contacts ?? 0) },
];

const TAG_COLS = [
  { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', ellipsis: true, sorter: (a, b) => a.Campaign.localeCompare(b.Campaign) },
  { title: 'Tag',      dataIndex: 'Tag',      key: 'Tag',      ellipsis: true, sorter: (a, b) => a.Tag.localeCompare(b.Tag) },
  { title: 'Count',    dataIndex: 'TagCount', key: 'TagCount', width: 90, align: 'right', sorter: (a, b) => (a.TagCount ?? 0) - (b.TagCount ?? 0) },
];

function buildTagPivot(rows, periodKey) {
  if (!rows.length) return { cols: [], data: [] };
  const periods = [...new Set(rows.map((r) => r[periodKey]))].sort();
  const combos  = [...new Set(rows.map((r) => `${r.Campaign}||${r.Tag}`))];
  const lookup  = {};
  rows.forEach((r) => { lookup[`${r.Campaign}||${r.Tag}||${r[periodKey]}`] = r.TagCount; });

  const cols = [
    { title: 'Campaign', dataIndex: '_campaign', key: '_campaign', width: 130, fixed: 'left', ellipsis: true },
    { title: 'Tag',      dataIndex: '_tag',      key: '_tag',      width: 160, fixed: 'left', ellipsis: true },
    ...periods.map((p) => ({
      title: p, dataIndex: p, key: p, width: 80, align: 'right',
      render: (v) => (v ?? '-'),
    })),
  ];

  const data = combos.map((ck) => {
    const [campaign, tag] = ck.split('||');
    const row = { _key: ck, _campaign: campaign, _tag: tag };
    periods.forEach((p) => { row[p] = lookup[`${ck}||${p}`] ?? null; });
    return row;
  });

  return { cols, data };
}

export default function CampaignDispoStatsPage() {
  const [dispoData,      setDispoData]      = useState([]);
  const [monthRows,      setMonthRows]      = useState([]);
  const [tagData,        setTagData]        = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [dispoMetric,    setDispoMetric]    = useState('Raw HT');
  const [tagView,        setTagView]        = useState('Tag Count');

  const handleSearch = async (filters) => {
    setLoading(true);
    try {
      const [dispoRes, tagRes] = await Promise.all([
        api.get('/api/campaign-dispo-stats/dispo-stats', { params: filters }),
        api.get('/api/campaign-dispo-stats/tag-stats',   { params: filters }),
      ]);
      setDispoData(dispoRes.data.rows ?? []);
      setMonthRows(dispoRes.data.month_rows ?? []);
      setTagData(tagRes.data);
    } catch {
      setDispoData([]);
      setMonthRows([]);
      setTagData(null);
    } finally {
      setLoading(false);
    }
  };

  const buildPivot = () => {
    if (!monthRows.length) return { pivotCols: [], pivotData: [] };

    const months  = [...new Set(monthRows.map((r) => r.Month))].sort();
    const dispos  = [...new Set(monthRows.map((r) => `${r.Campaign}||${r.Disp_Name}`))];
    const lookup  = {};
    monthRows.forEach((r) => { lookup[`${r.Campaign}||${r.Disp_Name}||${r.Month}`] = r; });

    const isAll     = dispoMetric === 'All';
    const metricKey = METRIC_KEY[dispoMetric];
    const fmt       = dispoMetric === 'Contacts' ? (v) => (v ?? '-') : (v) => (v != null ? fmtTime(v) : '-');

    const ALL_ROWS = [
      { label: 'HT',    get: (c) => fmtTime(c.AvgHT)   },
      { label: 'Talk',  get: (c) => fmtTime(c.AvgTalk)  },
      { label: 'ACW',   get: (c) => fmtTime(c.AvgACW)   },
      { label: 'Calls', get: (c) => c.Contacts ?? '-'    },
    ];

    const pivotCols = [
      { title: 'Campaign',  dataIndex: '_campaign', key: '_campaign', width: 120, fixed: 'left', ellipsis: true },
      { title: 'Disp Name', dataIndex: '_dispo',    key: '_dispo',    width: 160, fixed: 'left', ellipsis: true },
      ...months.map((m) => ({
        title: m, dataIndex: m, key: m, align: 'right', width: isAll ? 130 : 90,
        render: (v) => {
          if (!isAll) return fmt(v);
          if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
          return (
            <div style={{ lineHeight: 1.7 }}>
              {ALL_ROWS.map(({ label, get }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: '#8c8c8c', fontSize: 11 }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{get(v)}</span>
                </div>
              ))}
            </div>
          );
        },
      })),
    ];

    const pivotData = dispos.map((dk) => {
      const [campaign, dispo] = dk.split('||');
      const row = { _key: dk, _campaign: campaign, _dispo: dispo };
      months.forEach((m) => {
        const cell = lookup[`${dk}||${m}`];
        row[m] = isAll ? (cell || null) : (cell ? cell[metricKey] : null);
      });
      return row;
    });

    return { pivotCols, pivotData };
  };

  const { pivotCols, pivotData } = buildPivot();

  const tagMonthPivot = buildTagPivot(tagData?.tag_month_rows ?? [], 'Month');
  const tagDayPivot   = buildTagPivot(tagData?.tag_day_rows   ?? [], 'Day');

  const emptyText = 'Select a campaign and click Search.';

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Campaign Dispo Stats
      </Typography.Title>

      <FilterPanel
        sessionKey="campaign-dispo-stats"
        onSearch={handleSearch}
        loading={loading}
        requireCampaign
        defaultDateRange={[dayjs().subtract(1, 'month').startOf('month'), dayjs()]}
      />

      <Tabs
        items={[
          {
            key: 'dispo-stats',
            label: 'Disposition Stats',
            children: (
              <>
                <Table
                  rowKey={(r) => `${r.Campaign}-${r.Disp_Name}`}
                  dataSource={dispoData}
                  columns={DISPO_COLS}
                  loading={loading}
                  size="small"
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                  locale={{ emptyText }}
                />

                {pivotData.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <Typography.Text strong>Dispo by Month</Typography.Text>
                      <Segmented
                        options={METRIC_OPTIONS}
                        value={dispoMetric}
                        onChange={setDispoMetric}
                        size="small"
                      />
                    </div>
                    <Table
                      rowKey="_key"
                      dataSource={pivotData}
                      columns={pivotCols}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'tags',
            label: 'Tags',
            children: (
              <>
                <div style={{ marginBottom: 12 }}>
                  <Segmented
                    options={TAG_VIEWS}
                    value={tagView}
                    onChange={setTagView}
                    size="small"
                  />
                </div>

                {tagView === 'Tag Count' && (
                  <Table
                    rowKey={(r) => `${r.Campaign}||${r.Tag}`}
                    dataSource={tagData?.tag_rows ?? []}
                    columns={TAG_COLS}
                    loading={loading}
                    size="small"
                    pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                    locale={{ emptyText }}
                  />
                )}

                {tagView === 'By Month' && (
                  <Table
                    rowKey="_key"
                    dataSource={tagMonthPivot.data}
                    columns={tagMonthPivot.cols}
                    loading={loading}
                    size="small"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText }}
                  />
                )}

                {tagView === 'By Day' && (
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      Capped at 90 days from filter start date.
                    </Typography.Text>
                    <Table
                      rowKey="_key"
                      dataSource={tagDayPivot.data}
                      columns={tagDayPivot.cols}
                      loading={loading}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      locale={{ emptyText }}
                    />
                  </>
                )}
              </>
            ),
          },
        ]}
      />
    </>
  );
}
